import type { ReactNode } from "react";
import classNames from "classnames/bind";
import { Colors, Icon } from "main";

import styles from "./Alert.module.css";

const cx = classNames.bind(styles);

export type AlertColor = "danger" | "success" | "warning" | "info";

type AlertProps = {
  color: AlertColor;
  title?: string;
  showIcon?: boolean;
  className?: string;
  onDismiss?: () => void;
  children?: ReactNode;
};

export function Alert(props: AlertProps) {
  const { color, title, showIcon = false, children, className, onDismiss = null } = props;

  const icon = getIcon(color);

  /********* Render ********/
  return (
    <div className={`${styles["alert-box"]} ${styles[color]} ${className || ""}`}>
      <div className={cx(`${styles["alert-box-grid"]}`)}>
        {/* The alert-box-icon class is used to center the icon vertically for larger body content */}
        {showIcon && icon && <div css={{ display: "flex", alignItems: "start" }}>{icon}</div>}

        {/* Title */}
        {title && (
          <div
            className={cx({
              [`${styles["alert-box-header"]}`]: true,
              [`${styles["col-span-full"]}`]: !showIcon && !onDismiss,
              [`${styles["col-start-1"]}`]: !showIcon && onDismiss,
              [`${styles["col-start-2"]}`]: showIcon && !onDismiss,
              [`${styles["col-span-2"]}`]: (showIcon || onDismiss) && !(showIcon && onDismiss),
            })}
          >
            <h5
              css={{
                marginLeft: 0,
                marginRight: 0,
                marginTop: 0,
                marginBottom: 0,
                // Override the font size for h5 tags
                fontSize: "16px !important",
                fontWeight: 600,
              }}
            >
              {title}
            </h5>
          </div>
        )}
        {title && <CloseButton onDismiss={onDismiss} />}

        {/* Content */}
        {children && (
          <div
            css={{
              fontSize: 16,
              fontWeight: 400,
            }}
            className={cx({
              [`${styles["alert-box-body"]}`]: true,
              [`${styles["col-span-full"]}`]: !showIcon && !onDismiss,
              [`${styles["col-start-1"]}`]: !showIcon && onDismiss,
              [`${styles["col-start-2"]}`]: showIcon,
              [`${styles["col-span-1"]}`]: showIcon && onDismiss,
              [`${styles["col-span-2"]}`]: (showIcon || onDismiss) && !(showIcon && onDismiss),
            })}
          >
            {children}
          </div>
        )}

        {!title && <CloseButton onDismiss={onDismiss} />}
      </div>
    </div>
  );
}

/********* Helper Components ********/
type CloseButtonProps = {
  onDismiss: (() => void) | null;
};

function CloseButton(props: CloseButtonProps) {
  const { onDismiss } = props;

  if (!onDismiss) return null;

  return (
    <button onClick={onDismiss} css={{ backgroundColor: "transparent" }}>
      <Icon name="Close" />
    </button>
  );
}

/********* Helper Functions ********/
function getIcon(alertColor: AlertColor): JSX.Element {
  switch (alertColor) {
    case "danger":
      return <Icon name="Alert" color={Colors.red500} />;
    case "success":
      return <Icon name="Check Circle" color={Colors.green500} />;
    case "warning":
      return <Icon name="Alert" color={Colors.yellow600} />;
    case "info":
      return <Icon name="Info" color={Colors.purple500} />;
  }
}
